import { addDays, levelFromPoints, type DimensionState, type LevelState } from '@selflevel/domain';
import { ENERGY_LABELS, MOOD_LABELS } from '../store/catalogo';
import { num } from '../ui/primitivas';

/* Lo que pintan la tarjeta del ritual y el tablero del día, derivado de los datos.
   Aquí no se decide nada nuevo: se lee el plan, el ánimo, la progresión y las
   identidades, y se dice lo que hay. */

/** Los cinco huecos de una escala de 1 a 5, llenos hasta el valor. */
export function segmentos(valor: number | null | undefined, total = 5): boolean[] {
  const n = valor ?? 0;
  return Array.from({ length: total }, (_, i) => i < n);
}

/**
 * «Con energía · ánimo bueno». La energía va primero porque es lo que el tablero
 * pinta en la barra; el ánimo la matiza. Media pregunta no es una lectura.
 */
export function lecturaDeAnimo(
  registro: { mood: number | null; energy: number | null } | null | undefined,
): string | null {
  if (!registro || registro.mood === null || registro.energy === null) return null;
  const energia = ENERGY_LABELS[registro.energy - 1];
  const animo = MOOD_LABELS[registro.mood - 1];
  if (!energia || !animo) return null;
  return `${energia.charAt(0).toUpperCase()}${energia.slice(1)} · ánimo ${animo}`;
}

/** Hitos hechos y totales por meta. Una meta sin hitos no entra: «0/0» no es un dato. */
export function hitosPorMeta(
  hitos: readonly { goalId: string; doneAt: string | null }[] | undefined,
): Map<string, { hechos: number; total: number }> {
  const mapa = new Map<string, { hechos: number; total: number }>();
  for (const h of hitos ?? []) {
    const actual = mapa.get(h.goalId) ?? { hechos: 0, total: 0 };
    mapa.set(h.goalId, {
      hechos: actual.hechos + (h.doneAt ? 1 : 0),
      total: actual.total + 1,
    });
  }
  return mapa;
}

/** La frase de identidad vigente de un área: la más reciente sin retirar. */
export function identidadDeArea(
  identidades:
    | readonly { areaId: string; text: string; adoptedAt: string; retiredAt: string | null }[]
    | undefined,
  areaId: string,
): string | null {
  let elegida: { text: string; adoptedAt: string } | null = null;
  for (const i of identidades ?? []) {
    if (i.areaId !== areaId || i.retiredAt) continue;
    if (elegida === null || i.adoptedAt > elegida.adoptedAt) elegida = i;
  }
  return elegida?.text ?? null;
}

/**
 * El nivel de un área. Una dimensión solo existe cuando ha entrado algún punto,
 * así que un área a cero es el nivel de cero puntos, no un hueco. Pero sin
 * progresión todavía no se sabe nada: `null`, y la pantalla no pinta un «Nv 1» con
 * la barra vacía que un instante después salta al nivel de verdad.
 */
export function nivelDeArea(
  dimensiones: readonly DimensionState[] | undefined,
  areaId: string,
): LevelState | null {
  if (dimensiones === undefined) return null;
  return dimensiones.find((d) => d.key === areaId)?.level ?? levelFromPoints(0);
}

/**
 * El nombre del último hito si se desbloqueó en los últimos `dias` (hoy incluido).
 * Manda la fecha del desbloqueo: el orden del catálogo no dice cuándo pasó.
 */
export function logroReciente(
  desbloqueos: readonly { name: string; localDate: string; unlockedAt: string }[] | undefined,
  hoy: string,
  dias = 7,
): string | null {
  const desde = addDays(hoy, -(dias - 1));
  let ultimo: { name: string; unlockedAt: string } | null = null;
  for (const d of desbloqueos ?? []) {
    if (d.localDate < desde || d.localDate > hoy) continue;
    if (ultimo === null || d.unlockedAt > ultimo.unlockedAt) ultimo = d;
  }
  return ultimo?.name ?? null;
}

export function puntosDelDia(puntos: number, nivel: number): string {
  return `Hoy llevas ${num(puntos)} ${puntos === 1 ? 'punto' : 'puntos'} · nivel ${nivel}`;
}
